export const DEFAULT_PHONE_COUNTRY_CODE = "+49";

// Split validation for the trainee application input.
const COUNTRY_CODE_REGEX = /^\+[1-9]\d{0,2}$/;
const PHONE_NUMBER_REGEX = /^[1-9]\d{3,14}$/;
const MIN_PHONE_DIGITS = 4;

// E.164-like validation for a full international phone number in one field.
const INTERNATIONAL_PHONE_REGEX = /^\+[1-9]\d{4,14}$/;
const INTERNATIONAL_TRUNK_PREFIX_REGEX = /\(\s*0\s*\)/g;
const MAX_E164_DIGITS = 15;

const removeInternationalTrunkPrefixMarker = (rawValue: string): string => {
  if (!rawValue.startsWith("+") && !rawValue.startsWith("00")) {
    return rawValue;
  }

  return rawValue.replace(INTERNATIONAL_TRUNK_PREFIX_REGEX, "");
};

export const sanitizeCountryCode = (value: string | null | undefined): string => {
  const digits = (value ?? "").replace(/\D/g, "").slice(0, 3);
  return digits ? `+${digits}` : "";
};

export const sanitizePhoneNumber = (value: string | null | undefined): string => {
  return (value ?? "").replace(/\D/g, "");
};

export const combinePhoneNumber = (
  countryCode: string | null | undefined,
  phoneNumber: string | null | undefined,
  defaultCountryCode = DEFAULT_PHONE_COUNTRY_CODE
): string => {
  const sanitizedCountryCode = sanitizeCountryCode(countryCode) || defaultCountryCode;
  const sanitizedPhoneNumber = sanitizePhoneNumber(phoneNumber);

  if (!sanitizedPhoneNumber) {
    return "";
  }

  return `${sanitizedCountryCode}${sanitizedPhoneNumber}`;
};

export const sanitizeInternationalPhoneNumber = (value: string | null | undefined): string => {
  const rawValue = removeInternationalTrunkPrefixMarker((value ?? "").trim());
  const digits = rawValue.replace(/\D/g, "");

  if (!digits) {
    return "";
  }

  if (rawValue.startsWith("+")) {
    return `+${digits}`;
  }

  if (digits.startsWith("00")) {
    return `+${digits.slice(2)}`;
  }

  return digits;
};

export const isValidCountryCode = (value: string | null | undefined): boolean => {
  return COUNTRY_CODE_REGEX.test((value ?? "").trim());
};

export const isValidPhoneNumber = (
  value: string | null | undefined,
  countryCode?: string | null | undefined
): boolean => {
  const sanitizedPhoneNumber = sanitizePhoneNumber(value);

  if (!PHONE_NUMBER_REGEX.test(sanitizedPhoneNumber)) {
    return false;
  }

  if (!countryCode) {
    return sanitizedPhoneNumber.length <= MAX_E164_DIGITS;
  }

  const sanitizedCountryCode = sanitizeCountryCode(countryCode);
  if (!isValidCountryCode(sanitizedCountryCode)) {
    return false;
  }

  return sanitizedCountryCode.replace("+", "").length + sanitizedPhoneNumber.length <= MAX_E164_DIGITS;
};

export const isValidInternationalPhoneNumber = (value: string | null | undefined): boolean => {
  return INTERNATIONAL_PHONE_REGEX.test(sanitizeInternationalPhoneNumber(value));
};

export const getMaxPhoneDigits = (countryCode?: string | null | undefined): number => {
  const sanitizedCountryCode = sanitizeCountryCode(countryCode ?? "");

  if (!isValidCountryCode(sanitizedCountryCode)) {
    return MAX_E164_DIGITS;
  }

  return MAX_E164_DIGITS - sanitizedCountryCode.replace("+", "").length;
};

export const getPhoneNumberValidationMessage = (
  value: string | null | undefined,
  countryCode?: string | null | undefined
): string => {
  const sanitizedPhoneNumber = sanitizePhoneNumber(value ?? "");
  const maxPhoneDigits = getMaxPhoneDigits(countryCode);

  if (sanitizedPhoneNumber.length > maxPhoneDigits) {
    return `Die Handynummer ist mit dieser Ländervorwahl zu lang. Erlaubt sind höchstens ${maxPhoneDigits} Ziffern.`;
  }

  if (sanitizedPhoneNumber.startsWith("0")) {
    return "Bitte gib die Handynummer im internationalen Format ein, also ohne führende 0.";
  }

  if (sanitizedPhoneNumber.length < MIN_PHONE_DIGITS) {
    return `Die Handynummer ist zu kurz. Bitte gib mindestens ${MIN_PHONE_DIGITS} Ziffern ein.`;
  }

  return "Bitte gib die Handynummer im internationalen Format ein, also ohne führende 0.";
};

export const getInternationalPhoneValidationMessage = (value: string | null | undefined): string => {
  const sanitizedPhoneNumber = sanitizeInternationalPhoneNumber(value);
  const digitCount = sanitizedPhoneNumber.replace("+", "").length;

  if (digitCount > MAX_E164_DIGITS) {
    return `Die Handynummer ist zu lang. Erlaubt sind höchstens ${MAX_E164_DIGITS} Ziffern nach dem +.`;
  }

  return "Bitte gib die Handynummer im internationalen Format ein, z. B. +491761234567.";
};

export const formatPhoneNumber = (phoneNumber: string | null | undefined): string => {
  const sanitizedPhoneNumber = sanitizeInternationalPhoneNumber(phoneNumber);

  if (!sanitizedPhoneNumber) {
    return "";
  }

  if (sanitizedPhoneNumber.startsWith("+")) {
    return sanitizedPhoneNumber;
  }

  if (sanitizedPhoneNumber.startsWith("0")) {
    return `+49${sanitizedPhoneNumber.slice(1)}`;
  }

  return `+49${sanitizedPhoneNumber}`;
};
